import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { Observable, from, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth/auth.service';
import { Location } from '@angular/common';
import { RoleType } from '../shared/enums/role-type/role-type.module';
import { CommonService } from '../services/shared/common/common.service';
import { Jwt } from '../models/jwt.model';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {

  constructor(
    private authService: AuthService,
    private msalService: MsalService,
    private router: Router,
    private location: Location,
    private commonService: CommonService
  ) { }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    const jwt: Jwt | null = this.authService.getJwt();

    var pathname = window.location.pathname;
    if (pathname.length > 1 && pathname != '/auth' && pathname != '/sso')
      this.authService.setPathName(window.location.pathname);

    if (this.commonService.isCustomerPage()) {
      const sso = this.getSSOValueIfCustomerPath(jwt);
      if (sso && sso.length > 0) {
        if (sso.toLowerCase() != 'solocal') {
          return new Observable<boolean | UrlTree>((observer) => {
            this.authService.createSSOToken(sso!)
              .pipe(
                map(response => {
                  if (response) {
                    observer.next(true);
                    observer.complete();
                  } else {
                    observer.next(this.router.parseUrl(''));
                    observer.complete();
                  }
                })
              ).subscribe({
                error: () => {
                  observer.next(this.router.parseUrl(''));
                  observer.complete();
                },
              });
          });
        } else {
          //treat here
          return this.authService.handleSSORedirect(pathname).pipe(
            map(path => {
              if (path === '/login') {
                return this.router.parseUrl(path);
              }
              return path !== '';
            })
          );
        }
      } else {
        if (this.authService.getToken().length > 0) {
          return of(true);
        } else {
          return of(this.router.parseUrl(''));
        }
      }
    }


    return from(this.authService.getUserRole()).pipe(
      switchMap((userRole) => {
        if (this.msalService.instance.getAllAccounts().length <= 0 || !this.authService.getToken() || this.authService.getToken()!.length <= 0) {
          if (userRole !== null) {
            if (this.commonService.isCustomerPage()) {
              return of(true);
            } else {
              return of(this.router.parseUrl(''));
            }
          } else {
            if (this.commonService.isCustomerPage()) {
              return of(this.router.parseUrl('/login'));
            } else {
              return of(this.router.parseUrl(''));
            }
          }
        }

        const expectedRole: RoleType = route.data['role'] ? route.data['role'] : undefined;

        if (userRole === null || userRole === undefined) {
          if(pathname != '/sso')
          {
            this.authService.setPathName(pathname);
          }

          if (this.commonService.isCustomerPage()) {
            return of(this.router.parseUrl('/login'));
          } else {
            return of(this.router.parseUrl(''));
          }

        } else if (expectedRole === undefined || userRole === expectedRole) {
          return of(true);
        } else {
          return of(this.router.parseUrl(''));
        }
      })
    );
  }

  getSSOValueIfCustomerPath(jwt: Jwt | null): string | null {
    const fullPath = this.location.path();
    // Check if the first segment of the path is 'customer'
    if (this.commonService.isCustomerPage()) {
      // Parse the query string to extract parameters
      const queryParams = new URLSearchParams(fullPath.split('?')[1]);

      // Check if the 'sso' parameter exists and return its value
      if (queryParams.has('sso')) {
        var sso = queryParams.get('sso');

        // prevent an admin logging as a customer
        var isAdminLoggingAsCustomer = jwt != null && jwt.role != null && jwt.role == RoleType.ADMIN && sso != 'solocal';
        if(isAdminLoggingAsCustomer)
        {
          return null;
        }

        //if is not a customer logging as an admin
        var isNotCustomerReloggingAsAdmin = jwt != null && jwt.role != null && jwt.role != RoleType.CUSTOMER && sso == 'solocal';
        if(!isNotCustomerReloggingAsAdmin){
          this.authService.removeToken();
          this.authService.removeCustomer();
        }

        return sso;
      }
    }
    // Return null if conditions are not met or 'sso' parameter does not exist
    return null;
  }
}
